import { useRef, useEffect, useCallback, useMemo } from 'react';
import type { RefObject } from 'react';
import * as d3 from 'd3';
import { DataSet, ColumnId, ReferenceLine, ContourLine } from '../../types';
import type { DensityDisplayMode, DensityVisualization, DensityBandwidth, DensityColorScale } from '../../types/graph-builder.types';
import { getColumnData, getColumnNameById } from '../utils/column-utils';
import { COLORS } from '../constants/colors';
import { createDefaultCategoricalScale } from '../constants/palette-utils';
import logger from '../utils/logger';
import {
  calculateBandwidth,
  calculateDensityThreshold,
  estimatePointDensity,
  applyVisualizationStyle,
  getDensityColorInterpolator,
  type DataPoint
} from '../utils/density-utils';

interface ScatterPlotOptions {
  margin?: { top: number; right: number; bottom: number; left: number };
  pointRadius?: number;
  pointHoverRadius?: number;
  enableTooltip?: boolean;
  xDomain?: [number, number];  // 初期X軸表示範囲
  yDomain?: [number, number];  // 初期Y軸表示範囲
  compact?: boolean;  // コンパクト表示（軸ラベル非表示）

  // 密度可視化の設定
  displayMode?: DensityDisplayMode;  // 表示モード（デフォルト: 'points'）
  densityVisualization?: DensityVisualization;  // 密度の可視化スタイル（デフォルト: 'filled-with-lines'）
  densityBandwidth?: DensityBandwidth;  // バンド幅（デフォルト: 'auto'）
  contourLevels?: number;  // 等高線のレベル数（デフォルト: 10）
  densityColorScale?: DensityColorScale;  // 密度のカラースケール（デフォルト: 'YlOrRd'）
}

interface D3Elements {
  svg?: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  g?: d3.Selection<SVGGElement, unknown, null, undefined>;
  chartGroup?: d3.Selection<SVGGElement, unknown, null, undefined>;
  xScale?: d3.ScaleLinear<number, number>;
  yScale?: d3.ScaleLinear<number, number>;
  xAxis?: d3.Selection<SVGGElement, unknown, null, undefined>;
  yAxis?: d3.Selection<SVGGElement, unknown, null, undefined>;
  xLabel?: d3.Selection<SVGTextElement, unknown, null, undefined>;
  yLabel?: d3.Selection<SVGTextElement, unknown, null, undefined>;
  tooltip?: d3.Selection<HTMLDivElement, unknown, HTMLElement, undefined>;
  clipPath?: string;
  legendGroup?: d3.Selection<SVGGElement, unknown, null, undefined>;
  densityGroup?: d3.Selection<SVGGElement, unknown, null, undefined>;  // 密度等高線グループ
}

/**
 * ScatterPlotのD3.js描画ロジックを管理するカスタムフック
 * 
 * @param svgRef SVG要素のref
 * @param dataset データセット
 * @param xColumn X軸のカラムID
 * @param yColumn Y軸のカラムID
 * @param width チャートの幅
 * @param height チャートの高さ
 * @param filteredRowIndices フィルタされた行インデックス
 * @param options 描画オプション
 * @returns データポイント、スケール、D3要素
 */
export function useD3ScatterPlot(
  svgRef: RefObject<SVGSVGElement | null>,
  dataset: DataSet | null,
  xColumn: ColumnId,
  yColumn: ColumnId,
  width: number,
  height: number,
  filteredRowIndices?: number[] | null,
  colorColumn?: ColumnId,
  options: ScatterPlotOptions = {}
) {
  const {
    margin: customMargin,
    pointRadius = 4,
    pointHoverRadius = 5,
    enableTooltip = true,
    xDomain: customXDomain,
    yDomain: customYDomain,
    compact = false,
    displayMode = 'points',
    densityVisualization = 'filled-with-lines',
    densityBandwidth = 'auto',
    contourLevels = 10,
    densityColorScale = 'YlOrRd'
  } = options;

  // マージン計算（compactモードでは軸ラベル用のスペースが不要だが、tickラベル分は確保する）
  const margin = customMargin ?? (compact
    ? { top: 20, right: 20, bottom: 30, left: 40 }
    : { top: 45, right: colorColumn ? 120 : 20, bottom: 40, left: 50 });

  // D3要素の参照を保持
  const d3Refs = useRef<D3Elements>({});
  
  // クリップパスのIDを保持
  const clipId = useRef(`scatter-clip-${Math.random().toString(36).substring(2, 11)}`);

  // チャート領域のサイズ
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // データポイントの計算（色情報を含む）
  const validPoints = useMemo(() => {
    if (!dataset) {
      return [];
    }
    
    const xData = getColumnData(dataset, xColumn);
    const yData = getColumnData(dataset, yColumn);
    const colorData = colorColumn ? getColumnData(dataset, colorColumn) : null;
    
    if (!xData || !yData) {
      logger.warn('[useD3ScatterPlot] Column data not found', {
        xColumn,
        yColumn,
        hasXData: !!xData,
        hasYData: !!yData
      });
      return [];
    }

    // フィルタリング適用
    const activeRowIndices = filteredRowIndices || Array.from({ length: dataset.rowCount || 0 }, (_, i) => i);
    
    // 有効なデータポイントを抽出（nullを除外）
    const points: Array<{x: number, y: number, index: number, category?: string | number | boolean}> = [];
    
    activeRowIndices.forEach(rowIndex => {
      const xValue = xData[rowIndex];
      const yValue = yData[rowIndex];
      const colorValue = colorData ? colorData[rowIndex] : undefined;
      
      if (typeof xValue === 'number' && typeof yValue === 'number' && 
          !isNaN(xValue) && !isNaN(yValue)) {
        points.push({ 
          x: xValue, 
          y: yValue, 
          index: rowIndex,
          category: colorValue as string | number | boolean | undefined
        });
      }
    });

    if (points.length === 0) {
      logger.warn('[useD3ScatterPlot] No valid data points found', {
        xColumn,
        yColumn,
        datasetRowCount: dataset.rowCount,
        activeRowIndicesCount: activeRowIndices.length
      });
    }

    return points;
  }, [dataset, xColumn, yColumn, colorColumn, filteredRowIndices]);

  // カラム名の取得
  const { xColumnName, yColumnName } = useMemo(() => {
    if (!dataset) return { xColumnName: '', yColumnName: '' };
    
    const xName = getColumnNameById(dataset, xColumn) || '';
    const yName = getColumnNameById(dataset, yColumn) || '';
    
    return { xColumnName: xName, yColumnName: yName };
  }, [dataset, xColumn, yColumn]);

  // スケールの計算（カラースケールを含む）
  const { xScale, yScale, colorScale } = useMemo(() => {
    // カスタムdomainが指定されている場合はそれを使用
    if (customXDomain && customYDomain) {
      const xSc = d3.scaleLinear()
        .domain(customXDomain)
        .range([0, innerWidth]);

      const ySc = d3.scaleLinear()
        .domain(customYDomain)
        .range([innerHeight, 0]);

      return { xScale: xSc, yScale: ySc, colorScale: null };
    }

    // データが存在しない場合、エラーが起きないようなデフォルトスケールを返す
    if (validPoints.length === 0) {
      const xSc = d3.scaleLinear()
        .domain([0, 1])
        .range([0, innerWidth]);

      const ySc = d3.scaleLinear()
        .domain([0, 1])
        .range([innerHeight, 0]);

      return { xScale: xSc, yScale: ySc, colorScale: null };
    }

    const xExtent = d3.extent(validPoints, d => d.x) as [number, number];
    const yExtent = d3.extent(validPoints, d => d.y) as [number, number];

    // カスタムdomainが指定されている場合は部分的に使用
    let xDomain = customXDomain || xExtent;
    let yDomain = customYDomain || yExtent;

    // extentが同じ値の場合（すべての点が同じ値）、範囲を広げる
    if (!customXDomain && xExtent[0] === xExtent[1]) {
      const padding = Math.abs(xExtent[0]) * 0.1 || 1;
      xDomain = [xExtent[0] - padding, xExtent[1] + padding];
    }

    if (!customYDomain && yExtent[0] === yExtent[1]) {
      const padding = Math.abs(yExtent[0]) * 0.1 || 1;
      yDomain = [yExtent[0] - padding, yExtent[1] + padding];
    }

    const xSc = d3.scaleLinear()
      .domain(xDomain)
      .range([0, innerWidth]);

    // Y軸のスケール：range([innerHeight, 0])により、小さい値が下、大きい値が上になる
    const ySc = d3.scaleLinear()
      .domain(yDomain)
      .range([innerHeight, 0]);

    // カラースケールの作成
    let colorSc: d3.ScaleOrdinal<string | number, string> | null = null;
    if (colorColumn && validPoints.length > 0) {
      const categories = Array.from(new Set(validPoints.map(d => String(d.category ?? 'undefined'))));
      // 統一カラーパレットシステムのデフォルトパレットを使用
      colorSc = createDefaultCategoricalScale(categories);
    }


    return { xScale: xSc, yScale: ySc, colorScale: colorSc };
  }, [validPoints, innerWidth, innerHeight, colorColumn, customXDomain, customYDomain]);

  // 初期化（一度だけ実行）
  useEffect(() => {
    if (!svgRef.current) return;

    // 幅が0の場合は初期化しない（actualWidthがまだ設定されていない）
    if (width === 0) return;

    // すでに初期化済みの場合はスキップ
    if (d3Refs.current.svg) return;

    const svg = d3.select(svgRef.current);

    d3Refs.current.svg = svg;

    // クリップパスを作成
    svg.append('defs')
      .append('clipPath')
      .attr('id', clipId.current)
      .append('rect');

    // メイングループを作成
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    d3Refs.current.g = g;

    // 軸グループを作成
    d3Refs.current.xAxis = g.append('g')
      .attr('class', 'x-axis');
    
    d3Refs.current.yAxis = g.append('g')
      .attr('class', 'y-axis');

    // 軸ラベルを作成（compactモードでは作成しない）
    if (!compact) {
      d3Refs.current.xLabel = g.append('text')
        .attr('class', 'x-label')
        .style('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', '#333');

      d3Refs.current.yLabel = g.append('text')
        .attr('class', 'y-label')
        .attr('transform', 'rotate(-90)')
        .style('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('fill', '#333');
    }

    // チャートエリアグループ（クリップパス適用）
    const chartGroup = g.append('g')
      .attr('clip-path', `url(#${clipId.current})`);
    d3Refs.current.chartGroup = chartGroup;

    // ツールチップ作成
    if (enableTooltip) {
      d3.select('body').selectAll('.scatter-tooltip').remove();
      const tooltip = d3.select('body').append('div')
        .attr('class', 'scatter-tooltip')
        .style('position', 'absolute')
        .style('visibility', 'hidden')
        .style('background-color', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '6px')
        .style('border-radius', '4px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('z-index', '1000');
      d3Refs.current.tooltip = tooltip;
    }

    // クリップパスIDを保存
    d3Refs.current.clipPath = clipId.current;

    return () => {
      // クリーンアップ
      if (enableTooltip) {
        d3.select('body').selectAll('.scatter-tooltip').remove();
      }
    };
  }, [svgRef, margin.left, margin.top, enableTooltip, width, compact]);

  // スケールをrefに保存（スケールが変わったときに更新）
  useEffect(() => {
    d3Refs.current.xScale = xScale;
    d3Refs.current.yScale = yScale;
  }, [xScale, yScale]);

  // サイズ更新
  useEffect(() => {
    const { svg, xAxis, xLabel, yLabel } = d3Refs.current;
    if (!svg || !xAxis) return;

    // SVGサイズを更新
    svg.attr('width', width).attr('height', height);

    // クリップパスのサイズを更新
    svg.select(`#${clipId.current} rect`)
      .attr('width', innerWidth)
      .attr('height', innerHeight);

    // 軸の位置を更新
    xAxis.attr('transform', `translate(0,${innerHeight})`);

    // 軸ラベルの位置を更新
    xLabel?.attr('x', innerWidth / 2)
      .attr('y', innerHeight + 35);

    yLabel?.attr('x', -innerHeight / 2)
      .attr('y', -35);
  }, [width, height, innerWidth, innerHeight]);

  // 軸ラベルの更新
  useEffect(() => {
    const { xLabel, yLabel } = d3Refs.current;
    
    xLabel?.text(xColumnName);
    yLabel?.text(yColumnName);
  }, [xColumnName, yColumnName]);

  // 凡例の更新
  useEffect(() => {
    const { svg } = d3Refs.current;
    if (!svg || !colorScale || !colorColumn) {
      // 凡例が不要な場合は削除
      if (svg) {
        svg.selectAll('.legend-group').remove();
      }
      return;
    }

    // 既存の凡例を削除
    svg.selectAll('.legend-group').remove();

    // 凡例グループを作成（ボタンを避けるため追加で15px下にオフセット）
    const legendGroup = svg.append('g')
      .attr('class', 'legend-group')
      .attr('transform', `translate(${width - margin.right + 10}, ${margin.top + 15})`);

    const categories = colorScale.domain();
    const legendItemHeight = 20;

    // 凡例のタイトル
    const colorColumnName = dataset ? getColumnNameById(dataset, colorColumn) || 'Category' : 'Category';
    legendGroup.append('text')
      .attr('x', 0)
      .attr('y', -5)
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text(colorColumnName);

    // 凡例項目
    const legendItems = legendGroup.selectAll('.legend-item')
      .data(categories)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (_d, i) => `translate(0, ${i * legendItemHeight + 10})`);

    // 色の矩形
    legendItems.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 12)
      .attr('height', 12)
      .style('fill', d => colorScale(d));

    // ラベル
    legendItems.append('text')
      .attr('x', 16)
      .attr('y', 9)
      .style('font-size', '11px')
      .style('fill', '#333')
      .text(d => d);

    d3Refs.current.legendGroup = legendGroup;
  }, [colorScale, colorColumn, dataset, width, margin.right, margin.top]);

  // ポイントを描画する関数
  const drawPoints = useCallback((
    visibleXScale: d3.ScaleLinear<number, number>,
    visibleYScale: d3.ScaleLinear<number, number>,
    selectedRows: number[] = [],
    onPointClick?: (dataIndices: number[], isAdditive?: boolean) => void
  ) => {
    const { chartGroup, tooltip } = d3Refs.current;
    if (!chartGroup) return;

    // displayModeに応じてフィルタリングされた点を決定
    let pointsToRender = validPoints;

    if (displayMode === 'both') {
      // 両方モード：低密度の点のみを描画
      if (validPoints.length >= 3) {
        // バンド幅を計算
        const dataPoints: DataPoint[] = validPoints.map(p => ({
          x: p.x,
          y: p.y,
          index: p.index
        }));

        const bandwidth = calculateBandwidth(
          densityBandwidth,
          dataPoints,
          visibleXScale,
          visibleYScale
        );

        // 密度の閾値を計算
        const threshold = calculateDensityThreshold(
          dataPoints,
          bandwidth,
          visibleXScale,
          visibleYScale
        );

        // 低密度の点のみをフィルタリング
        pointsToRender = validPoints.filter(point => {
          const density = estimatePointDensity(
            { x: point.x, y: point.y, index: point.index },
            dataPoints,
            bandwidth,
            visibleXScale,
            visibleYScale
          );
          return density < threshold;
        });
      }
    } else if (displayMode === 'density') {
      // 密度のみモード：点を描画しない
      pointsToRender = [];
    } else if (displayMode === 'points-with-density') {
      // 全点+密度モード：全点を描画
      pointsToRender = validPoints;
    }
    // displayMode === 'points' の場合も全点を描画（デフォルト）

    // データポイントを更新（データバインディング）- keyにStringを使用して一意性を保証
    const circles = chartGroup.selectAll<SVGCircleElement, typeof validPoints[0]>('.scatter-point')
      .data(pointsToRender, d => String(d.index));

    // 削除される要素
    circles.exit().remove();

    // 新規要素のみに初期属性を設定
    const enter = circles.enter()
      .append('circle')
      .attr('class', 'scatter-point')
      .attr('r', pointRadius)
      .style('cursor', 'pointer')
      .attr('cx', d => visibleXScale(d.x))
      .attr('cy', d => visibleYScale(d.y))
      .style('fill', d => {
        // カラースケールがある場合は使用、ない場合はデフォルト色
        if (colorScale && d.category !== undefined) {
          return colorScale(String(d.category));
        }
        return COLORS.primary.blue;
      });

    // 既存要素を更新（位置と色）
    circles
      .attr('cx', d => visibleXScale(d.x))
      .attr('cy', d => visibleYScale(d.y))
      .style('fill', d => {
        // カラースケールがある場合は使用、ない場合はデフォルト色
        if (colorScale && d.category !== undefined) {
          return colorScale(String(d.category));
        }
        return COLORS.primary.blue;
      });
    
    // 既存要素と新規要素をマージ
    const merged = circles.merge(enter);

    // イベントハンドラー設定
    if (enableTooltip && tooltip) {
      merged
        .on('mouseover', function(_event, d) {
          // スケール適用後の画面座標を計算
          const screenX = visibleXScale(d.x);
          const screenY = visibleYScale(d.y);

          // 同じ画面座標（±1px）の点を検出
          const epsilon = 1;
          const overlappingPoints = pointsToRender.filter(p => {
            const px = visibleXScale(p.x);
            const py = visibleYScale(p.y);
            return Math.abs(px - screenX) < epsilon && Math.abs(py - screenY) < epsilon;
          });

          // ツールチップの基本内容
          let tooltipContent = `${xColumnName}: ${d.x.toFixed(2)}<br/>${yColumnName}: ${d.y.toFixed(2)}`;
          if (d.category !== undefined && colorColumn) {
            tooltipContent += `<br/>Category: ${d.category}`;
          }

          // 重複がある場合、追加情報を表示
          if (overlappingPoints.length > 1) {
            tooltipContent += '<hr style="margin: 4px 0; border: none; border-top: 1px solid rgba(255,255,255,0.3);">';
            tooltipContent += `Points at this location: ${overlappingPoints.length}`;

            // 5点以下の場合は行インデックスも表示
            if (overlappingPoints.length <= 5) {
              const indices = overlappingPoints.map(p => p.index).join(', ');
              tooltipContent += `<br/>Row indices: ${indices}`;
            }
          }

          tooltip.style('visibility', 'visible')
            .html(tooltipContent);
          d3.select(this).attr('r', pointHoverRadius);
        })
        .on('mousemove', function(event) {
          tooltip.style('top', (event.pageY - 10) + 'px')
            .style('left', (event.pageX + 10) + 'px');
        })
        .on('mouseout', function(_event, d) {
          tooltip.style('visibility', 'hidden');
          d3.select(this).attr('r', selectedRows.includes(d.index) ? pointHoverRadius : pointRadius);
        });
    }

    if (onPointClick) {
      merged.on('click', function(event, d) {
        const isAdditive = event.ctrlKey || event.metaKey;
        onPointClick([d.index], isAdditive);
      });
    }

    return merged;
  }, [validPoints, xColumnName, yColumnName, pointRadius, pointHoverRadius, enableTooltip, colorScale, colorColumn, displayMode, densityBandwidth]);

  // 軸を更新する関数
  const updateAxes = useCallback((
    visibleXScale: d3.ScaleLinear<number, number>,
    visibleYScale: d3.ScaleLinear<number, number>
  ) => {
    const { xAxis, yAxis } = d3Refs.current;
    if (!xAxis || !yAxis) return;

    xAxis.call(d3.axisBottom(visibleXScale).ticks(5))
      .style('color', '#666')
      .style('font-size', '11px');
    xAxis.selectAll('path, line')
      .style('stroke', '#ccc');

    yAxis.call(d3.axisLeft(visibleYScale).ticks(5))
      .style('color', '#666')
      .style('font-size', '11px');
    yAxis.selectAll('path, line')
      .style('stroke', '#ccc');
  }, []);

  // ブラシグループを作成する関数
  const createBrushGroup = useCallback(() => {
    const { svg } = d3Refs.current;
    if (!svg) return null;

    // 既存のブラシグループを削除
    svg.selectAll('.brush').remove();

    // 新しいブラシグループを作成
    const brushGroup = svg.append('g')
      .attr('class', 'brush');
    
    return brushGroup;
  }, []);

  // 参照線を描画する関数
  const drawReferenceLines = useCallback((
    lines: ReferenceLine[],
    visibleXScale: d3.ScaleLinear<number, number>,
    visibleYScale: d3.ScaleLinear<number, number>
  ) => {
    const { chartGroup } = d3Refs.current;
    if (!chartGroup || !lines || lines.length === 0) return;
    
    // 既存の参照線をクリア
    chartGroup.selectAll('.reference-line-group').remove();
    
    // 参照線グループを作成（chartGroup内で、scatter-pointの前に配置）
    let refGroup;
    const scatterPoints = chartGroup.select('.scatter-point');
    if (!scatterPoints.empty()) {
      // scatter-pointが存在する場合、その前に挿入
      refGroup = chartGroup.insert('g', '.scatter-point')
        .attr('class', 'reference-line-group');
    } else {
      // scatter-pointがまだ存在しない場合、最初に追加
      refGroup = chartGroup.append('g')
        .attr('class', 'reference-line-group');
    }
    
    const [xMin, xMax] = visibleXScale.domain();
    const [yMin, yMax] = visibleYScale.domain();
    
    lines.forEach((line, index) => {
      let x1: number, y1: number, x2: number, y2: number;
      let diagonalMax: number = 0;  // diagonal用の変数をスコープ外で宣言
      
      switch (line.type) {
        case 'horizontal':
          if (line.value === undefined) {
            logger.warn('[useD3ScatterPlot] Horizontal line missing value');
            return;
          }
          x1 = visibleXScale(xMin);
          y1 = visibleYScale(line.value);
          x2 = visibleXScale(xMax);
          y2 = visibleYScale(line.value);
          break;
          
        case 'vertical':
          if (line.value === undefined) {
            logger.warn('[useD3ScatterPlot] Vertical line missing value');
            return;
          }
          x1 = visibleXScale(line.value);
          y1 = visibleYScale(yMin);
          x2 = visibleXScale(line.value);
          y2 = visibleYScale(yMax);
          break;
          
        case 'diagonal': {
          // y = x の線を描画（表示範囲内で）
          const min = Math.max(xMin, yMin);
          diagonalMax = Math.min(xMax, yMax);
          x1 = visibleXScale(min);
          y1 = visibleYScale(min);
          x2 = visibleXScale(diagonalMax);
          y2 = visibleYScale(diagonalMax);
          break;
        }
          
        default:
          logger.warn('[useD3ScatterPlot] Unknown reference line type:', line.type);
          return;
      }
      
      const lineElement = refGroup.append('line')
        .attr('class', `reference-line-${index}`)
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .style('stroke', line.style?.stroke || '#999')
        .style('stroke-width', line.style?.strokeWidth || 1)
        .style('opacity', line.style?.opacity || 0.7)
        .style('pointer-events', 'none');  // クリックイベントを透過
      
      // stroke-dasharrayは値がある場合のみ設定
      if (line.style?.strokeDasharray) {
        lineElement.style('stroke-dasharray', line.style.strokeDasharray);
      }
      
      // ラベルの追加（オプション）
      if (line.label) {
        // ラベルの位置を計算
        let labelX: number, labelY: number;
        let textAnchor = 'start';
        let dx = 5;
        let dy = -5;
        
        switch (line.type) {
          case 'horizontal':
            labelX = visibleXScale(xMax);
            labelY = visibleYScale(line.value!);
            textAnchor = 'end';
            dx = -5;
            break;
          case 'vertical':
            labelX = visibleXScale(line.value!);
            labelY = visibleYScale(yMax);
            dy = 15;
            textAnchor = 'middle';
            dx = 0;
            break;
          case 'diagonal':
            labelX = visibleXScale(diagonalMax);
            labelY = visibleYScale(diagonalMax);
            textAnchor = 'end';
            dx = -5;
            break;
        }
        
        refGroup.append('text')
          .attr('class', `reference-line-label-${index}`)
          .attr('x', labelX)
          .attr('y', labelY)
          .attr('dx', dx)
          .attr('dy', dy)
          .style('text-anchor', textAnchor)
          .style('font-size', '11px')
          .style('fill', line.style?.stroke || '#999')
          .style('opacity', line.style?.opacity || 0.7)
          .style('pointer-events', 'none')
          .text(line.label);
      }
    });

  }, []);

  // 等高線を描画する関数
  const drawContourLines = useCallback((
    lines: ContourLine[],
    visibleXScale: d3.ScaleLinear<number, number>,
    visibleYScale: d3.ScaleLinear<number, number>
  ) => {
    const { chartGroup } = d3Refs.current;
    if (!chartGroup || !lines || lines.length === 0) return;

    // 既存の等高線をクリア
    chartGroup.selectAll('.contour-line-group').remove();

    // 等高線グループを作成（chartGroup内で、scatter-pointの前に配置）
    let contourGroup;
    const scatterPoints = chartGroup.select('.scatter-point');
    if (!scatterPoints.empty()) {
      // scatter-pointが存在する場合、その前に挿入（reference-lineの後）
      const refLineGroup = chartGroup.select('.reference-line-group');
      if (!refLineGroup.empty()) {
        contourGroup = chartGroup.insert('g', '.scatter-point')
          .attr('class', 'contour-line-group');
      } else {
        contourGroup = chartGroup.insert('g', '.scatter-point')
          .attr('class', 'contour-line-group');
      }
    } else {
      // scatter-pointがまだ存在しない場合、最初に追加
      contourGroup = chartGroup.append('g')
        .attr('class', 'contour-line-group');
    }

    // D3のline generatorを作成
    const lineGenerator = d3.line<{ x: number; y: number }>()
      .x(d => visibleXScale(d.x))
      .y(d => visibleYScale(d.y));

    lines.forEach((line, index) => {
      if (!line.points || line.points.length === 0) {
        logger.warn('[useD3ScatterPlot] Contour line missing points');
        return;
      }

      // 曲線を描画
      const pathElement = contourGroup.append('path')
        .attr('class', `contour-line-${index}`)
        .datum(line.points)
        .attr('d', lineGenerator)
        .style('fill', 'none')
        .style('stroke', line.style?.stroke || '#999')
        .style('stroke-width', line.style?.strokeWidth || 1)
        .style('opacity', line.style?.opacity || 0.5)
        .style('pointer-events', 'none');  // クリックイベントを透過

      // stroke-dasharrayは値がある場合のみ設定
      if (line.style?.strokeDasharray) {
        pathElement.style('stroke-dasharray', line.style.strokeDasharray);
      }

      // ラベルの追加（オプション）- 曲線の終端に配置
      if (line.label && line.points.length > 0) {
        const lastPoint = line.points[line.points.length - 1];
        contourGroup.append('text')
          .attr('class', `contour-line-label-${index}`)
          .attr('x', visibleXScale(lastPoint.x))
          .attr('y', visibleYScale(lastPoint.y))
          .attr('dx', 5)
          .attr('dy', -5)
          .style('text-anchor', 'start')
          .style('font-size', '11px')
          .style('fill', line.style?.stroke || '#999')
          .style('opacity', line.style?.opacity || 0.5)
          .style('pointer-events', 'none')
          .text(line.label);
      }
    });

  }, []);

  // 密度等高線を描画する関数
  const drawDensityContours = useCallback((
    visibleXScale: d3.ScaleLinear<number, number>,
    visibleYScale: d3.ScaleLinear<number, number>
  ) => {
    const { chartGroup } = d3Refs.current;
    if (!chartGroup) return;

    // displayModeが'points'の場合は密度描画をスキップ
    if (displayMode === 'points') {
      chartGroup.selectAll('.density-contour-group').remove();
      return;
    }

    // 'density', 'both', 'points-with-density'の場合は密度を描画

    // データポイントが少なすぎる場合は密度計算をスキップ
    if (validPoints.length < 3) {
      logger.warn('[useD3ScatterPlot] Not enough data points for density visualization', {
        pointCount: validPoints.length
      });
      chartGroup.selectAll('.density-contour-group').remove();
      return;
    }

    // 既存の密度等高線をクリア
    chartGroup.selectAll('.density-contour-group').remove();

    // 密度等高線グループを作成（scatter-pointの前に配置）
    let densityGroup;
    const scatterPoints = chartGroup.select('.scatter-point');
    if (!scatterPoints.empty()) {
      densityGroup = chartGroup.insert('g', '.scatter-point')
        .attr('class', 'density-contour-group');
    } else {
      densityGroup = chartGroup.append('g')
        .attr('class', 'density-contour-group');
    }

    d3Refs.current.densityGroup = densityGroup;

    // バンド幅を計算
    const dataPoints: DataPoint[] = validPoints.map(p => ({
      x: p.x,
      y: p.y,
      index: p.index
    }));

    const bandwidth = calculateBandwidth(
      densityBandwidth,
      dataPoints,
      visibleXScale,
      visibleYScale
    );

    // d3.contourDensity()を使用して密度等高線を生成
    const densityFunction = d3.contourDensity<DataPoint>()
      .x(d => visibleXScale(d.x))
      .y(d => visibleYScale(d.y))
      .size([innerWidth, innerHeight])
      .bandwidth(bandwidth)
      .thresholds(contourLevels);

    const contours = densityFunction(dataPoints);

    // 密度値の範囲を取得してカラースケールを作成
    const densityExtent = d3.extent(contours, d => d.value) as [number, number];
    const colorInterpolator = getDensityColorInterpolator(densityColorScale);
    const colorScale = d3.scaleSequential(colorInterpolator)
      .domain(densityExtent);

    // 等高線パスをレンダリング
    const paths = densityGroup.selectAll<SVGPathElement, d3.ContourMultiPolygon>('path')
      .data(contours)
      .enter()
      .append('path')
      .attr('d', d3.geoPath());

    // 可視化スタイルを適用
    applyVisualizationStyle(paths, densityVisualization, colorScale);

    // ポインターイベントを透過（点のクリックを妨げない）
    paths.style('pointer-events', 'none');

  }, [validPoints, displayMode, densityVisualization, densityBandwidth, contourLevels, densityColorScale, innerWidth, innerHeight]);

  return {
    // データ
    validPoints,
    xColumnName,
    yColumnName,

    // スケール
    xScale,
    yScale,
    colorScale,

    // サイズ
    innerWidth,
    innerHeight,
    margin,

    // D3要素への参照
    d3Elements: d3Refs.current,
    clipId: clipId.current,

    // 描画関数
    drawPoints,
    updateAxes,
    createBrushGroup,
    drawReferenceLines,
    drawContourLines,
    drawDensityContours
  };
}